import parse, {attributesToProps, DOMNode, domToReact, Element} from "html-react-parser";
import React, {useEffect, useState} from "react";
import {useAuth} from "../auth/AuthHook";
import {useErrorReporter} from "../general/ErrorViewer";
import {resolve_relative} from "../general/Resolve_relative";
import {TitleBox} from "../general/TitleBox";
import {useTranslation} from "react-i18next";
import {useParams} from "react-router-dom";
import {useSupportCase} from "../support/CaseHook";
import {FetchError} from "../support/utils";


interface DocViewerProps {
    doc_prefix: string
    internal_prefix: string
}

const formattingOptions = (path: string | undefined, doc_prefix: string) => {
    return {
        replace(domNode: DOMNode) {
            return formatElement(domNode, path, doc_prefix);
        }
    }
}


function formatElement(domNode: DOMNode, path: string | undefined, doc_prefix: string) {
    if ((domNode instanceof Element) && domNode.name === 'img' && path != null) {
        const props = attributesToProps(domNode.attribs);
        const {src, alt} = props;

        if (src == null || typeof src != 'string') {
            return domNode;
        }
        if (src.startsWith("http://") || src.startsWith("https://")) {
            return domNode;
        }

        const abs_src = path.startsWith("/") ?
            resolve_relative(`${path}`, src) :
            resolve_relative(`${doc_prefix}/${path}`, src);
        const rest_props = {...props};
        delete rest_props.src;
        delete rest_props.alt;
        return <img className="py-6" src={abs_src} alt={`${alt ?? ""}`} {...rest_props} />;
    }
    const options = formattingOptions(path, doc_prefix);
    if ((domNode instanceof Element) && domNode.name === "h1") {
        return <h1 className="text-4xl py-4 font-bold" {...attributesToProps(domNode.attribs)}>
            {domToReact(domNode.children as DOMNode[], options)}
        </h1>
    }
    if ((domNode instanceof Element) && domNode.name === "h2") {
        return <h2 className="text-3xl py-4 font-bold" {...attributesToProps(domNode.attribs)}>
            {domToReact(domNode.children as DOMNode[], options)}
        </h2>
    }
    if ((domNode instanceof Element) && domNode.name === "h3") {
        return <h3 className="text-2xl py-4 " {...attributesToProps(domNode.attribs)}>
            {domToReact(domNode.children as DOMNode[], options)}
        </h3>
    }
    if ((domNode instanceof Element) && domNode.name === "h4") {
        return <h4 className="text-xl underline py-4 " {...attributesToProps(domNode.attribs)}>
            {domToReact(domNode.children as DOMNode[], options)}
        </h4>
    }
    if ((domNode instanceof Element) && domNode.name === "ol") {
        return <ol className="list-decimal" {...attributesToProps(domNode.attribs)}>
            {domToReact(domNode.children as DOMNode[], options)}
        </ol>
    }
    if ((domNode instanceof Element) && domNode.name === "table") {
        const children = domNode.children as DOMNode[];
        return <table className="border border-collapse" {...attributesToProps(domNode.attribs)}>
            {domToReact(children, options)}
        </table>
    }
    if ((domNode instanceof Element) && domNode.name === "td") {
        return <td className="border" {...attributesToProps(domNode.attribs)}>
            {domToReact(domNode.children as DOMNode[], options)}
        </td>
    }
    if ((domNode instanceof Element) && domNode.name === "div" && domNode.attribs.class ==="codehilite") {
        const attributes = domNode.attribs;
        delete attributes.class;
        return (
            <div role="code-container" className="flex flex-col w-full overflow-auto">
                <div className="codehilite flex flex-col grow overflow-auto" {...attributesToProps(attributes)}>
                    {domToReact(domNode.children as DOMNode[], options)}
                </div>
            </div>
        )
    }
    return domNode;
}

export function DocViewer({doc_prefix, internal_prefix}: DocViewerProps) {
    const {isLoggedIn} = useAuth();
    const {fetchData} = useSupportCase()
    const [doc, setDoc] = useState<string | null>(null)
    const reportError = useErrorReporter()
    const {t} = useTranslation();
    const docPath = useParams()["*"];

    useEffect(() => {
        const fetchDoc = async () => {
            if (!isLoggedIn()) {
                setDoc("<p>You need to log in to view this document</p>")
                return
            }
            try {
                console.log(`Fetching doc from ${internal_prefix}${doc_prefix}/${docPath} ...`)
                const response = await fetchData(`${internal_prefix}${doc_prefix}/${docPath}`, "GET")
                const text = await response.text()
                setDoc(text)
            } catch (e) {
                console.error(`Error fetching document:`, e);
                if (e instanceof FetchError) {
                    if (e.response.status === 404) {
                        setDoc("<div class='text-center'></div><h1>Not found</h1>\n\n<p>The referenced document was not found. Sorry about the inconvenience.</p></div>")
                        return
                    }
                }
            }

        }
        if (docPath != null) {
            fetchDoc()
        }
    }, [docPath, doc_prefix, internal_prefix, isLoggedIn, reportError, fetchData])

    const doc_as_html = parse(doc ?? "# Loading ...", formattingOptions(docPath, doc_prefix));
    return (
        docPath == null ? <></> :
            <div id="docviewer-container" className={"flex flex-col p-6 2xl:p-14 items-left w-full overflow-auto"}>
                <TitleBox lineHidden={true}>
                    <div className="text-2xl text-white text-center">
                        <div>{t("Reference document")}</div>

                        <div>{docPath?.split("/").pop()}</div>
                    </div>

                </TitleBox>
                <div className="flex flex-col items-left leading-loose">
                    {doc_as_html}
                </div>
            </div>
    )
}